import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import Countdown from './components/Countdown';
import { socket } from './socket';
import type {
  AckResponse,
  MarriageAnonymousAnswer,
  MarriageGender,
  MarriageSettings,
  MarriageState,
  MarriageTask,
  Player
} from './types';

interface Props {
  self: Player;
  room: MarriageState;
  task: MarriageTask | null;
  notify: (message: string) => void;
}

export default function BlindMarriageRouter({ self, room, task, notify }: Props) {
  const [eliminatedRound, setEliminatedRound] = useState<number | null>(
    task?.kind === 'marriage-eliminated' ? task.eliminatedRound : null
  );

  useEffect(() => {
    if (task?.kind === 'marriage-eliminated') setEliminatedRound(task.eliminatedRound);
    if (room.phase === 'marriage_lobby') setEliminatedRound(null);
  }, [task, room.phase]);

  useEffect(() => {
    const onEliminated = (payload?: { round?: number }) => {
      const round = Number(payload?.round || room.round || 1);
      setEliminatedRound(round);
      notify(`تم استبعادك في الجولة ${round}. بقيت هويتك سرية حتى النهاية 👀`);
    };
    socket.on('marriage:eliminated', onEliminated);
    return () => { socket.off('marriage:eliminated', onEliminated); };
  }, [notify, room.round]);

  const content = (() => {
    switch (room.phase) {
      case 'marriage_lobby': return <MarriageLobby self={self} room={room} notify={notify} />;
      case 'marriage_proposing': return <ProposalStage self={self} room={room} task={task} notify={notify} />;
      case 'marriage_proposal_closed': return <ProposalClosed self={self} room={room} task={task} notify={notify} />;
      case 'marriage_asking': return <QuestionStage self={self} room={room} task={task} notify={notify} eliminatedRound={eliminatedRound} />;
      case 'marriage_answering': return <AnswerStage self={self} room={room} task={task} notify={notify} eliminatedRound={eliminatedRound} />;
      case 'marriage_revealing': return <RevealStage self={self} room={room} />;
      case 'marriage_eliminating': return <EliminationStage self={self} room={room} task={task} notify={notify} />;
      case 'marriage_elimination_reveal': return <EliminationReveal room={room} />;
      case 'marriage_finale': return <FinaleStage self={self} room={room} notify={notify} />;
      default: return <MarriageWaiting title="جاري تجهيز الجولة" text="لحظات وتبدأ المرحلة التالية." />;
    }
  })();

  return (
    <section className="marriage-game-shell">
      {self.isChef && room.phase !== 'marriage_lobby' && room.phase !== 'marriage_finale' && (
        <MarriageChefLiveControls room={room} notify={notify} />
      )}
      {room.phase !== 'marriage_lobby' && <StarBanner room={room} />}
      {content}
    </section>
  );
}

function MarriageLobby({ self, room, notify }: { self: Player; room: MarriageState; notify: (message: string) => void }) {
  const [settings, setSettings] = useState<MarriageSettings>(room.marriageSettings);
  const [busy, setBusy] = useState(false);
  useEffect(() => setSettings(room.marriageSettings), [room.marriageSettings]);

  const profiles = useMemo(() => new Map(room.profiles.map((profile) => [profile.playerId, profile.gender])), [room.profiles]);
  const players = room.players.filter((player) => !player.isSpectator && !player.isChef);
  const spectators = room.players.filter((player) => player.isSpectator);
  const selected = Boolean(room.starId);

  const patch = <K extends keyof MarriageSettings>(key: K, value: MarriageSettings[K]) => setSettings((current) => ({ ...current, [key]: value }));

  const save = async () => {
    const response = await emitMarriageAck('chef:marriageSettings', settings);
    if (!response.ok) throw new Error(response.error || 'تعذر حفظ الإعدادات.');
  };

  const selectStar = async (targetId: string) => {
    try {
      const response = await emitMarriageAck('chef:marriageSelectStar', { targetId });
      if (!response.ok) throw new Error(response.error || 'تعذر اختيار اللاعب.');
    } catch (error) { notify(messageOf(error)); }
  };

  const start = async () => {
    setBusy(true);
    try {
      await save();
      const response = await emitMarriageAck('chef:marriageStartProposals', {});
      if (!response.ok) throw new Error(response.error || 'تعذر فتح باب التقدم.');
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  return (
    <div className="marriage-lobby">
      <header className="glass-card marriage-hero-card">
        <div className="marriage-hero-copy">
          <span className="marriage-kicker">💍 BLIND MARRIAGE</span>
          <h1>الحب أعمى… <em>والإجابات أخطر.</em></h1>
          <p>اختيار سري بالكامل: يتقدم اللاعبون بدون أسماء، تجيب الشخصيات المجهولة عن الأسئلة، وفي كل جولة يخرج صاحب إجابة واحدة حتى يبقى الاختيار الأخير.</p>
          <div className="marriage-rule-chips"><span>🕶️ هويات مخفية</span><span>💌 تقديم سري</span><span>❓ أسئلة مباشرة</span><span>🔥 إقصاء تدريجي</span></div>
        </div>
        <div className="marriage-hero-rings" aria-hidden="true"><span>💍</span><i>♥</i><b>?</b></div>
      </header>

      <div className={`marriage-lobby-grid ${self.isChef ? 'with-admin' : ''}`}>
        <section className="glass-card marriage-players-card">
          <div className="marriage-card-head"><div><span className="eyebrow">المشاركون</span><h2>من سيكون نجم الليلة؟</h2></div><span className="live-badge"><i /> مباشر</span></div>
          {self.isChef ? (
            <>
              <div className="marriage-player-grid">
                {players.map((player, index) => {
                  const gender = profiles.get(player.id) ?? 'male';
                  const isSelected = player.id === room.starId;
                  return (
                    <article key={player.id} className={`marriage-player-card ${isSelected ? 'selected-star' : ''} ${!player.connected ? 'offline' : ''}`}>
                      <div className={`marriage-avatar ${gender}`} style={{ '--marriage-index': index } as CSSProperties}>{gender === 'female' ? '👰' : '🤵'}</div>
                      <div><strong>{player.nickname}</strong><small>{gender === 'female' ? 'أنثى' : 'ذكر'} · {player.connected ? 'جاهز' : 'منقطع'}</small></div>
                      {isSelected && <span className="marriage-selected-crown">👑 المختار</span>}
                      {!isSelected && player.connected && <button type="button" className="marriage-pick-btn" onClick={() => selectStar(player.id)}>اختيار</button>}
                    </article>
                  );
                })}
              </div>
              {!players.length && <div className="marriage-empty"><span>💌</span><strong>بانتظار اللاعبين</strong><p>يلزم وجود عروس/عريس ومتقدمين اثنين على الأقل.</p></div>}
              {spectators.length > 0 && <div className="marriage-spectator-note">👁️ {spectators.length} متفرج/متفرجين في الغرفة</div>}
              {selected && <SelectedStarPreview room={room} />}
            </>
          ) : (
            <>
              <AnonymousLobbyRoster room={room} />
              {selected && <SelectedStarPreview room={room} />}
              <MarriageWaiting title="بانتظار الأدمن" text={selected ? 'تم اختيار نجم الليلة. الأسماء الأخرى تبقى مخفية بالكامل حتى الكشف النهائي.' : 'الأدمن وحده يرى أسماء اللاعبين وسيختار العروس أو العريس.'} />
            </>
          )}
        </section>

        {self.isChef && (
          <aside className="glass-card marriage-settings-card">
            <div className="marriage-card-head"><div><span className="eyebrow">👑 إعدادات الأدمن</span><h2>إيقاع اللعبة</h2></div><span className="marriage-pro-badge">LIVE</span></div>
            <MarriageRange label="وقت التقدم" value={settings.proposalSeconds} min={15} max={180} suffix="ث" onChange={(value) => patch('proposalSeconds', value)} />
            <MarriageRange label="وقت الإجابة" value={settings.answerSeconds} min={10} max={120} suffix="ث" onChange={(value) => patch('answerSeconds', value)} />
            <label className="marriage-setting-block"><span><strong>سرعة كشف الإجابات</strong><small>{(settings.revealIntervalMs / 1000).toFixed(1)} ثانية بين كل إجابة</small></span><input type="range" min="500" max="3000" step="100" value={settings.revealIntervalMs} onChange={(event) => patch('revealIntervalMs', Number(event.target.value))} /></label>
            <div className="marriage-admin-note"><b>🔐 خصوصية اللعبة</b><p>أسماء المتقدمين لا تُرسل إلى واجهات اللاعبين أثناء اللعب. الربط بين الإجابة وصاحبها يبقى على الخادم حتى النهاية.</p></div>
            {selected && (
              <MarriagePhotoUpload
                role="star"
                label={`صورة ${room.starGender === 'female' ? 'العروس' : 'العريس'} (اختيارية)`}
                ready={Boolean(room.starPhotoReady)}
                notify={notify}
              />
            )}
            <div className="marriage-admin-actions">
              <button type="button" className="secondary-btn" onClick={() => save().then(() => notify('تم حفظ إعدادات الزواج الأعمى.')).catch((error) => notify(messageOf(error)))}>حفظ</button>
              <button type="button" className="primary-btn xl" disabled={!room.starId || players.length < 3 || busy} onClick={start}>{busy ? 'فتح المسرح...' : 'فتح باب التقدم 💌'}</button>
            </div>
            {!room.starId && <p className="tiny-note">اختر العروس أو العريس أولًا.</p>}
          </aside>
        )}
      </div>
    </div>
  );
}

function ProposalStage({ self, room, task, notify }: { self: Player; room: MarriageState; task: MarriageTask | null; notify: (message: string) => void }) {
  const proposalTask = task?.kind === 'marriage-proposal' ? task : null;
  const [busy, setBusy] = useState(false);
  const isStar = self.id === room.starId;

  const propose = async () => {
    setBusy(true);
    try {
      const response = await emitMarriageAck('marriage:propose', {});
      if (!response.ok) throw new Error(response.error || 'تعذر تسجيل التقدم.');
      notify('تم تسجيل تقدمك سرًا 💌 لا أحد يعرف أنك من المتقدمين.');
    } catch (error) { notify(messageOf(error)); setBusy(false); }
  };

  return (
    <section className="marriage-stage glass-card proposal-stage">
      <div className="marriage-stage-icon">💌</div>
      <span className="eyebrow">المرحلة الأولى · التقديم السري</span>
      <h2>باب التقدم مفتوح</h2>
      <p>لا تظهر أسماء المتقدمين لأي شخص. يظهر فقط العدد الإجمالي.</p>
      {room.proposalEndsAt && <Countdown endsAt={room.proposalEndsAt} totalSeconds={room.marriageSettings.proposalSeconds} hourglass warningSound activityLabel="التقديم" />}
      <div className="proposal-counter"><strong>{room.proposalCount}</strong><span>طلب تقدم سري</span><div>{Array.from({ length: Math.min(12, room.proposalCount) }, (_, i) => <i key={i}>♥</i>)}</div></div>

      {isStar ? (
        <div className="marriage-private-message"><span>👑</span><strong>أنت محور اللعبة</strong><p>لن تعرف من تقدم لك. عندما ينتهي الوقت ستبدأ بطرح الأسئلة على الأصوات المجهولة.</p></div>
      ) : self.isSpectator || self.isChef ? (
        <MarriageWaiting title={self.isChef ? 'راقب العدد فقط' : 'أنت متفرج'} text="لن تظهر هوية أي متقدم خلال هذه المرحلة." />
      ) : proposalTask?.applied ? (
        <div className="proposal-success"><span>✓</span><h3>تم إرسال طلبك سرًا</h3><p>ابقَ هادئًا… اسمك لم يظهر لأحد.</p></div>
      ) : proposalTask ? (
        <button type="button" className="marriage-propose-btn" disabled={busy} onClick={propose}><span>💍</span><strong>{busy ? 'جاري الإرسال...' : 'أتقدم الآن'}</strong><small>الطلب مجهول بالكامل</small></button>
      ) : (
        <MarriageWaiting title="المقاعد مكتملة" text="أنت موجود في الغرفة للمشاهدة فقط في هذه الجولة." />
      )}
    </section>
  );
}

function ProposalClosed({ self, room, task, notify }: { self: Player; room: MarriageState; task: MarriageTask | null; notify: (message: string) => void }) {
  const reopen = async () => {
    try {
      const response = await emitMarriageAck('chef:marriageReopenProposals', {});
      if (!response.ok) throw new Error(response.error || 'تعذر إعادة فتح التقديم.');
    } catch (error) { notify(messageOf(error)); }
  };
  return (
    <section className="marriage-stage glass-card proposal-closed-stage">
      <div className="marriage-stage-icon">⏳</div>
      <span className="eyebrow">انتهى وقت التقديم</span>
      <h2>نحتاج متقدمين اثنين على الأقل</h2>
      <p>تم تسجيل <b>{room.proposalCount}</b> فقط. لن يتم كشف أي اسم.</p>
      {task?.kind === 'marriage-proposal' && task.applied && <div className="proposal-success compact">✓ طلبك ما زال محفوظًا سرًا</div>}
      {self.isChef ? <button type="button" className="primary-btn" onClick={reopen}>إعادة فتح التقديم {room.marriageSettings.proposalSeconds} ثانية 💌</button> : <MarriageWaiting title="بانتظار الأدمن" text="يمكن للأدمن تمديد وقت التقديم بدون كشف أي هوية." />}
    </section>
  );
}

function QuestionStage({ self, room, task, notify, eliminatedRound }: { self: Player; room: MarriageState; task: MarriageTask | null; notify: (message: string) => void; eliminatedRound: number | null }) {
  const askTask = task?.kind === 'marriage-ask' ? task : null;
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (value.trim().length < 3) return;
    setBusy(true);
    try {
      const response = await emitMarriageAck('marriage:submitQuestion', { value: value.trim() });
      if (!response.ok) throw new Error(response.error || 'تعذر إرسال السؤال.');
      setValue('');
    } catch (error) { notify(messageOf(error)); setBusy(false); }
  };

  if (askTask) {
    return (
      <section className="marriage-stage glass-card question-stage">
        <div className="round-badge">الجولة {askTask.round}</div>
        <div className="marriage-stage-icon">❓</div>
        <h2>اسأل المتقدمين المجهولين</h2>
        <p>السؤال سيظهر للجميع، لكن الإجابات ستبقى مخفية حتى يجيب الجميع أو ينتهي الوقت.</p>
        <form className="marriage-question-form" onSubmit={submit}>
          <textarea value={value} maxLength={500} placeholder="مثال: ما أكثر شيء يمكن أن يجعلك تضحك في يوم سيئ؟" onChange={(event) => setValue(event.target.value)} autoFocus />
          <div><small>{askTask.activeSuitorCount} متقدمين ما زالوا في اللعبة</small><button className="primary-btn" disabled={busy || value.trim().length < 3}>{busy ? 'جاري الإرسال...' : 'إرسال السؤال للجميع 💬'}</button></div>
        </form>
      </section>
    );
  }

  if (eliminatedRound) return <PrivateEliminatedCard round={eliminatedRound} />;
  return <MarriageWaiting title={`الجولة ${room.round}`} text="العروس/العريس يكتب السؤال الآن. السؤال سيظهر هنا فور إرساله." icon="✍️" />;
}

function AnswerStage({ self, room, task, notify, eliminatedRound }: { self: Player; room: MarriageState; task: MarriageTask | null; notify: (message: string) => void; eliminatedRound: number | null }) {
  const answerTask = task?.kind === 'marriage-answer' ? task : null;
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValue(''); setBusy(false); }, [room.round]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try {
      const response = await emitMarriageAck('marriage:submitAnswer', { value: value.trim() });
      if (!response.ok) throw new Error(response.error || 'تعذر إرسال الإجابة.');
      notify('تم إرسال إجابتك سرًا 🤫');
    } catch (error) { notify(messageOf(error)); setBusy(false); }
  };

  return (
    <section className="marriage-stage glass-card answer-stage">
      <div className="round-badge">الجولة {room.round}</div>
      <span className="eyebrow">سؤال للجميع</span>
      <blockquote className="marriage-public-question">“{room.question}”</blockquote>
      <div className="marriage-answer-meta"><span><b>{room.answerCount}</b> / {room.activeSuitorCount} أجابوا</span>{room.answerEndsAt && <Countdown endsAt={room.answerEndsAt} totalSeconds={room.marriageSettings.answerSeconds} hourglass warningSound activityLabel="الإجابة" />}</div>

      {answerTask ? (
        answerTask.submitted ? (
          <div className="proposal-success"><span>✓</span><h3>إجابتك محفوظة</h3><p>لن تظهر أي إجابة قبل انتهاء الوقت أو وصول الجميع.</p></div>
        ) : (
          <form className="marriage-answer-form" onSubmit={submit}><textarea value={value} maxLength={800} placeholder="اكتب إجابتك… لا تذكر اسمك 😉" onChange={(event) => setValue(event.target.value)} autoFocus /><button className="primary-btn xl" disabled={busy || !value.trim()}>{busy ? 'جاري الإرسال...' : 'إرسال الإجابة سرًا 🤫'}</button></form>
        )
      ) : eliminatedRound ? <PrivateEliminatedCard round={eliminatedRound} /> : self.id === room.starId ? (
        <MarriageWaiting title="الإجابات تصل الآن" text="لن ترى أي إجابة قبل أن يجيب الجميع أو ينتهي المؤقت." icon="🙈" />
      ) : <MarriageWaiting title={self.isSpectator || self.isChef ? 'أنت تتابع الجولة' : 'بانتظار المتقدمين'} text="السؤال ظاهر للجميع، لكن الإجابات محجوبة حتى لحظة الكشف." />}
    </section>
  );
}

function RevealStage({ room }: { self: Player; room: MarriageState }) {
  return (
    <section className="marriage-stage glass-card reveal-stage">
      <div className="round-badge">الجولة {room.round}</div>
      <span className="eyebrow">كشف الإجابات</span>
      <h2>واحدة… واحدة… بدون أسماء 👀</h2>
      <blockquote className="marriage-public-question small">“{room.question}”</blockquote>
      <div className="anonymous-answer-grid">
        {room.revealedAnswers.map((answer, index) => <AnonymousAnswerCard key={answer.id} answer={answer} index={index} />)}
        {Array.from({ length: Math.max(0, room.revealTotal - room.revealedAnswers.length) }, (_, index) => <div className="anonymous-answer-card hidden-card" key={`hidden-${index}`}><span>؟</span><p>إجابة مخفية…</p></div>)}
      </div>
      <div className="reveal-progress"><i style={{ width: `${room.revealTotal ? (room.revealedAnswers.length / room.revealTotal) * 100 : 0}%` }} /><span>{room.revealedAnswers.length} / {room.revealTotal}</span></div>
    </section>
  );
}

function EliminationStage({ self, room, task, notify }: { self: Player; room: MarriageState; task: MarriageTask | null; notify: (message: string) => void }) {
  const eliminateTask = task?.kind === 'marriage-eliminate' ? task : null;
  const [busy, setBusy] = useState<string | null>(null);

  const eliminate = async (answer: MarriageAnonymousAnswer) => {
    if (!window.confirm('استبعاد صاحب هذه الإجابة؟ لن يظهر اسمه الآن، وسيُكشف فقط في النهاية.')) return;
    setBusy(answer.id);
    try {
      const response = await emitMarriageAck('marriage:eliminate', { answerId: answer.id });
      if (!response.ok) throw new Error(response.error || 'تعذر تنفيذ الاستبعاد.');
    } catch (error) { notify(messageOf(error)); setBusy(null); }
  };

  return (
    <section className="marriage-stage glass-card elimination-stage">
      <div className="round-badge">الجولة {room.round}</div>
      <span className="eyebrow">لحظة القرار</span>
      <h2>{self.id === room.starId ? 'أي إجابة لم تعجبك؟' : 'من سيغادر هذه الجولة؟'}</h2>
      <p>{self.id === room.starId ? 'اختر الإجابة التي تريد استبعاد صاحبها. الهوية ستبقى مخفية.' : 'العروس/العريس يختار الآن إجابة واحدة للاستبعاد.'}</p>
      <div className="anonymous-answer-grid selectable">
        {room.revealedAnswers.map((answer, index) => (
          <div key={answer.id} className="anonymous-answer-choice">
            <AnonymousAnswerCard answer={answer} index={index} />
            {eliminateTask && <button type="button" disabled={busy !== null} onClick={() => eliminate(answer)}>{busy === answer.id ? 'جاري الاستبعاد...' : 'استبعاد صاحب الإجابة ✕'}</button>}
          </div>
        ))}
      </div>
      {!eliminateTask && <MarriageWaiting title="القرار بيد العروس/العريس" text="ستُظلّل الإجابة المستبعدة فور الاختيار، لكن اسم صاحبها سيبقى سرًا." icon="🔥" />}
    </section>
  );
}

function EliminationReveal({ room }: { room: MarriageState }) {
  return (
    <section className="marriage-stage glass-card elimination-reveal-stage">
      <div className="round-badge">الجولة {room.round}</div>
      <span className="eyebrow">تم الاستبعاد</span>
      <h2>إجابة واحدة خرجت من السباق</h2>
      <div className="anonymous-answer-grid">
        {room.revealedAnswers.map((answer, index) => <div key={answer.id} className={answer.id === room.lastEliminatedAnswerId ? 'eliminated-answer-wrap' : ''}><AnonymousAnswerCard answer={answer} index={index} />{answer.id === room.lastEliminatedAnswerId && <span className="eliminated-stamp">مستبعد ✕</span>}</div>)}
      </div>
      <p className="marriage-transition-copy">بقي {room.activeSuitorCount} في السباق. {room.activeSuitorCount > 1 ? 'السؤال التالي قادم…' : 'حان وقت كشف الاختيار الأخير!'}</p>
    </section>
  );
}

function FinaleStage({ self, room, notify }: { self: Player; room: MarriageState; notify: (message: string) => void }) {
  const finale = room.finale;
  if (!finale) return <MarriageWaiting title="لحظة الكشف" text="يتم تجهيز النتيجة النهائية…" icon="💍" />;

  const reset = async () => {
    if (!window.confirm('العودة إلى غرفة الانتظار وبدء لعبة جديدة؟')) return;
    try {
      const response = await emitMarriageAck('chef:marriageReset', {});
      if (!response.ok) throw new Error(response.error || 'تعذر إعادة اللعبة.');
    } catch (error) { notify(messageOf(error)); }
  };

  return (
    <section className="marriage-finale">
      <Fireworks />
      <div className="glass-card marriage-couple-card">
        <span className="marriage-finale-kicker">💍 الاختيار الأخير 💍</span>
        <h1>انتهى الغموض!</h1>
        <div className="final-couple">
          <FinalPerson name={finale.starNickname} gender={finale.starGender} role={finale.starGender === 'female' ? 'العروس' : 'العريس'} photo={finale.starPhoto} />
          <div className="final-heart"><span>♥</span><b>+</b></div>
          <FinalPerson name={finale.winnerNickname} gender={finale.winnerGender} role={finale.winnerGender === 'female' ? 'العروس الفائزة' : 'العريس الفائز'} winner photo={finale.winnerPhoto} />
        </div>
        <p>بعد {finale.rounds.length} {finale.rounds.length === 1 ? 'جولة' : 'جولات'} من الأسئلة والإجابات المجهولة، انكشفت الهوية أخيرًا.</p>
        {self.isChef && (
          <div className="marriage-finale-photo-admin">
            <div className="marriage-finale-photo-copy"><strong>📸 صور التتويج (اختيارية)</strong><small>تُحفظ مؤقتًا في الذاكرة فقط وتُحذف تلقائيًا عند بدء لعبة جديدة.</small></div>
            <div className="marriage-finale-photo-grid">
              <MarriagePhotoUpload role="star" label={`صورة ${finale.starGender === 'female' ? 'العروس' : 'العريس'}`} ready={Boolean(room.starPhotoReady)} photo={finale.starPhoto} notify={notify} compact />
              <MarriagePhotoUpload role="winner" label={`صورة ${finale.winnerGender === 'female' ? 'العروس الفائزة' : 'العريس الفائز'}`} ready={Boolean(room.winnerPhotoReady)} photo={finale.winnerPhoto} notify={notify} compact />
            </div>
          </div>
        )}
        {self.isChef && <button type="button" className="primary-btn xl centered" onClick={reset}>لعبة جديدة 💍</button>}
      </div>

      <section className="glass-card marriage-transcript-card">
        <div className="marriage-card-head"><div><span className="eyebrow">الكشف الكامل</span><h2>من قال ماذا؟ ومتى خرج؟</h2></div><span className="marriage-pro-badge">REVEALED</span></div>
        <div className="marriage-table-wrap">
          <table className="marriage-results-table">
            <thead><tr><th>المشارك</th>{finale.rounds.map((round) => <th key={round.round}>الجولة {round.round}<small>{round.question}</small></th>)}<th>النتيجة</th></tr></thead>
            <tbody>
              {finale.contestants.map((contestant) => (
                <tr key={contestant.playerId} className={contestant.isWinner ? 'winner-row' : ''}>
                  <td><span className="result-person-icon">{contestant.gender === 'female' ? '👩' : '👨'}</span><strong>{contestant.nickname}</strong></td>
                  {finale.rounds.map((round) => {
                    const answer = contestant.answers.find((item) => item.round === round.round);
                    return <td key={round.round}>{answer ? <span className={answer.timedOut ? 'timed-out-answer' : ''}>{answer.answer}</span> : <span className="no-answer">—</span>}</td>;
                  })}
                  <td>{contestant.isWinner ? <b className="winner-result">👑 الفائز</b> : <span className="out-result">خرج في الجولة {contestant.eliminatedRound}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="marriage-mobile-results">
          {finale.contestants.map((contestant, contestantIndex) => (
            <article key={contestant.playerId} className={`marriage-mobile-result-card ${contestant.isWinner ? 'winner' : ''}`}>
              <header>
                <div className="marriage-mobile-person">
                  <span>{contestant.isWinner ? '👑' : contestant.gender === 'female' ? '👩' : '👨'}</span>
                  <div><small>المشارك {contestantIndex + 1}</small><strong>{contestant.nickname}</strong></div>
                </div>
                <div className={`marriage-mobile-status ${contestant.isWinner ? 'winner' : ''}`}>
                  {contestant.isWinner ? 'الفائز ✨' : `خرج في الجولة ${contestant.eliminatedRound}`}
                </div>
              </header>

              <div className="marriage-mobile-answer-list">
                {finale.rounds.map((round) => {
                  const answer = contestant.answers.find((item) => item.round === round.round);
                  return (
                    <div className="marriage-mobile-answer" key={round.round}>
                      <div className="marriage-mobile-round-head"><b>الجولة {round.round}</b><span>{round.question}</span></div>
                      {answer ? <p className={answer.timedOut ? 'timed-out-answer' : ''}>{answer.answer}</p> : <p className="no-answer">— لم يعد مشاركًا في هذه الجولة</p>}
                    </div>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

function MarriageChefLiveControls({ room, notify }: { room: MarriageState; notify: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  const canAdvance = ['marriage_proposing', 'marriage_answering', 'marriage_revealing'].includes(room.phase);
  const label = room.phase === 'marriage_proposing' ? 'إنهاء التقديم الآن' : room.phase === 'marriage_answering' ? 'إنهاء وقت الإجابة' : room.phase === 'marriage_revealing' ? 'كشف كل الإجابات الآن' : '';

  const advance = async () => {
    setBusy(true);
    try {
      const response = await emitMarriageAck('chef:marriageAdvance', {});
      if (!response.ok) throw new Error(response.error || 'تعذر الانتقال.');
    } catch (error) { notify(messageOf(error)); setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm('إنهاء الزواج الأعمى الحالي والعودة إلى غرفة الانتظار؟')) return;
    setBusy(true);
    try {
      const response = await emitMarriageAck('chef:marriageReset', {});
      if (!response.ok) throw new Error(response.error || 'تعذر إنهاء اللعبة.');
    } catch (error) { notify(messageOf(error)); setBusy(false); }
  };

  return <div className="glass-card marriage-live-controls"><div><strong>👑 تحكم الأدمن</strong><small>{phaseLabel(room.phase)}</small></div><div>{canAdvance && <button type="button" className="secondary-btn" disabled={busy} onClick={advance}>{label} ⏭</button>}<button type="button" className="secondary-btn danger-outline" disabled={busy} onClick={reset}>إنهاء اللعبة</button></div></div>;
}

function StarBanner({ room }: { room: MarriageState }) {
  if (!room.starNickname) return null;
  const gender = room.starGender ?? 'female';
  return <div className={`marriage-star-banner ${gender}`}><div className="star-portrait"><span>{gender === 'female' ? '👰' : '🤵'}</span><i>👑</i></div><div><small>{gender === 'female' ? 'عروس الليلة' : 'عريس الليلة'}</small><strong>{room.starNickname}</strong><em>{room.phase === 'marriage_proposing' ? 'بانتظار المتقدمين المجهولين…' : room.phase === 'marriage_finale' ? 'تم الاختيار!' : `الجولة ${room.round || 1}`}</em></div><div className="star-banner-hearts">♥ ♥ ♥</div></div>;
}


function AnonymousLobbyRoster({ room }: { room: MarriageState }) {
  return (
    <div className="marriage-anonymous-roster">
      <div className="marriage-anonymous-lock"><span>🕶️</span><div><strong>الهويات مخفية</strong><small>الأدمن فقط يستطيع رؤية أسماء المشاركين.</small></div></div>
      <div className="marriage-anonymous-stats">
        <div><strong>{room.participantCount}</strong><span>لاعب</span></div>
        <div><strong>{room.spectatorCount}</strong><span>متفرج</span></div>
        <div><strong>{room.starId ? '1' : '0'}</strong><span>اسم ظاهر</span></div>
      </div>
    </div>
  );
}

function SelectedStarPreview({ room }: { room: MarriageState }) {
  const gender = room.starGender ?? 'female';
  return <div className={`selected-star-preview ${gender}`}><span className="selected-star-emoji">{gender === 'female' ? '👰' : '🤵'}</span><div><small>اختيار الأدمن</small><strong>👑 {room.starNickname}</strong><p>هذا هو الاسم الوحيد الذي يظهر للجميع أثناء اللعبة حتى الكشف النهائي.</p></div></div>;
}

function AnonymousAnswerCard({ answer, index }: { answer: MarriageAnonymousAnswer; index: number }) {
  return <article className={`anonymous-answer-card ${answer.timedOut ? 'timed-out' : ''}`}><span className="anonymous-number">#{index + 1}</span><div className="anonymous-mask">🎭</div><p>{answer.text}</p>{answer.timedOut && <small>انتهى الوقت</small>}</article>;
}

function PrivateEliminatedCard({ round }: { round: number }) {
  return <div className="private-eliminated-card"><span>🕶️</span><h3>خرجت من المنافسة</h3><p>تم استبعادك في الجولة {round}. هويتك ما زالت سرية، ويمكنك متابعة اللعبة كمتفرج حتى الكشف النهائي.</p></div>;
}

function FinalPerson({ name, gender, role, winner = false, photo }: { name: string; gender: MarriageGender; role: string; winner?: boolean; photo?: string }) {
  return <div className={`final-person ${winner ? 'winner' : ''} ${gender} ${photo ? 'has-photo' : ''}`}><div className="final-person-crown">👑</div><div className="final-person-avatar">{photo ? <img src={photo} alt={name} /> : (gender === 'female' ? '👰' : '🤵')}</div><small>{role}</small><strong>{name}</strong>{winner && <span>الاختيار النهائي ✨</span>}</div>;
}

function MarriagePhotoUpload({ role, label, ready, photo, notify, compact = false }: { role: 'star' | 'winner'; label: string; ready: boolean; photo?: string; notify: (message: string) => void; compact?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(photo ?? null);

  useEffect(() => {
    if (photo) setPreview(photo);
    else if (!ready) setPreview(null);
  }, [photo, ready]);

  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      const value = await compressMarriagePhoto(file);
      const response = await emitMarriageAck('chef:marriagePhoto', { role, value });
      if (!response.ok) throw new Error(response.error || 'تعذر حفظ الصورة المؤقتة.');
      setPreview(value);
      notify('تم تجهيز الصورة للتتويج 📸');
    } catch (error) {
      notify(messageOf(error));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      const response = await emitMarriageAck('chef:marriagePhoto', { role, value: null });
      if (!response.ok) throw new Error(response.error || 'تعذر حذف الصورة.');
      setPreview(null);
      notify('تم حذف الصورة المؤقتة.');
    } catch (error) {
      notify(messageOf(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`marriage-photo-upload ${compact ? 'compact' : ''}`}>
      <div className="marriage-photo-preview">{preview ? <img src={preview} alt={label} /> : <span>{role === 'star' ? '👑' : '💍'}</span>}</div>
      <div className="marriage-photo-upload-copy"><strong>{label}</strong><small>{ready ? '✓ صورة مؤقتة جاهزة' : 'JPG / PNG / WebP · تُحذف بعد اللعبة'}</small></div>
      <label className={`marriage-photo-button ${busy ? 'disabled' : ''}`}>
        <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={(event) => { const file = event.target.files?.[0]; void upload(file); event.currentTarget.value = ''; }} />
        {busy ? 'ضغط…' : ready ? 'تغيير' : 'إضافة صورة'}
      </label>
      {ready && <button type="button" className="marriage-photo-remove" disabled={busy} onClick={clear}>حذف</button>}
    </div>
  );
}

async function compressMarriagePhoto(file: File): Promise<string> {
  const allowed = new Set(['image/jpeg', 'image/png', 'image/webp']);
  if (!allowed.has(file.type)) throw new Error('استخدم صورة JPG أو PNG أو WebP فقط.');
  if (file.size > 8_000_000) throw new Error('حجم الصورة الأصلية كبير جدًا. اختر صورة أصغر من 8MB.');

  const image = await loadMarriageImage(file);
  const maxEdge = 720;
  const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('المتصفح لا يستطيع معالجة الصورة.');
  context.drawImage(image, 0, 0, width, height);

  let value = canvas.toDataURL('image/jpeg', 0.82);
  if (marriagePhotoBytes(value) > 900_000) value = canvas.toDataURL('image/jpeg', 0.68);
  if (marriagePhotoBytes(value) > 900_000) value = canvas.toDataURL('image/jpeg', 0.54);
  if (marriagePhotoBytes(value) > 900_000) throw new Error('تعذر ضغط الصورة للحجم المسموح. اختر صورة أصغر.');
  return value;
}

function loadMarriageImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('تعذر قراءة ملف الصورة.')); };
    image.src = url;
  });
}

function marriagePhotoBytes(value: string): number {
  const base64 = value.slice(value.indexOf(',') + 1);
  return Math.floor(base64.length * 3 / 4);
}

function Fireworks() {
  const bursts = [0, 1, 2, 3, 4];
  const particles = Array.from({ length: 14 }, (_, index) => index);
  return <div className="marriage-fireworks" aria-hidden="true">{bursts.map((burst) => <div className={`firework burst-${burst + 1}`} key={burst}>{particles.map((particle) => <i key={particle} style={{ '--particle': particle } as CSSProperties} />)}</div>)}</div>;
}

function MarriageWaiting({ title, text, icon = '💍' }: { title: string; text: string; icon?: string }) {
  return <div className="marriage-waiting"><span>{icon}</span><h3>{title}</h3><p>{text}</p><div className="marriage-dots"><i /><i /><i /></div></div>;
}

function MarriageRange({ label, value, min, max, suffix, onChange }: { label: string; value: number; min: number; max: number; suffix: string; onChange: (value: number) => void }) {
  return <label className="marriage-setting-block"><span><strong>{label}</strong><small>{value}{suffix}</small></span><input type="range" min={min} max={max} value={value} onChange={(event) => onChange(Number(event.target.value))} /></label>;
}

function phaseLabel(phase: MarriageState['phase']) {
  const labels: Record<MarriageState['phase'], string> = {
    marriage_lobby: 'غرفة الانتظار', marriage_proposing: 'باب التقدم السري مفتوح', marriage_proposal_closed: 'بانتظار متقدمين إضافيين', marriage_asking: 'كتابة سؤال جديد', marriage_answering: 'استقبال الإجابات السرية', marriage_revealing: 'كشف الإجابات تدريجيًا', marriage_eliminating: 'بانتظار قرار الإقصاء', marriage_elimination_reveal: 'عرض الإجابة المستبعدة', marriage_finale: 'الكشف النهائي'
  };
  return labels[phase];
}

function messageOf(error: unknown) { return error instanceof Error ? error.message : 'حدث خطأ غير متوقع.'; }

function emitMarriageAck(event: string, payload: unknown): Promise<AckResponse> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('لم يصل تأكيد الخادم في الوقت المتوقع. حاول مجددًا.'));
    }, 30_000);
    const send = () => socket.emit(event, payload, (response: AckResponse) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      socket.off('connect', send);
      resolve(response);
    });
    if (socket.connected) send();
    else { socket.once('connect', send); socket.connect(); }
  });
}
